'use client'

import { ClipboardList } from 'lucide-react'
import { CSSProperties, FC, useEffect, useRef, useState } from 'react'
import { WIDE_FONT_FAMILY } from './wideFont'

type WideSurveyEntryButtonProps = {
  total: number
  onOpen: () => void
  /** 置き場所ごとの位置指定（絶対配置・align-self など） */
  style?: CSSProperties
}

/**
 * ワイド型の任意アンケートの入口。ワイド型は入口が常に映像の上に重なるので、
 * 映像が白っぽくても黒っぽくても読めるよう、ボタン自体に濃い背景を持たせる。
 * 通常の再生中・選択肢の表示中のどちらもこの部品を使い、見た目を揃える。
 */
const WideSurveyEntryButton: FC<WideSurveyEntryButtonProps> = ({ total, onOpen, style }) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isCompact, setIsCompact] = useState(false)

  // 置かれた動画枠の幅で文言を短くする（選択肢の幕・操作バーと同じ 560px 境界）
  useEffect(() => {
    const frame = buttonRef.current?.offsetParent
    if (!(frame instanceof HTMLElement)) return
    const sync = () => setIsCompact(frame.getBoundingClientRect().width < 560)
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(frame)
    return () => observer.disconnect()
  }, [])

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onOpen}
      data-testid="wide-survey-entry"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        border: '1px solid rgba(255,255,255,0.22)',
        borderRadius: '999px',
        padding: isCompact ? '5px 10px' : '9px 13px',
        background: 'rgba(8,18,38,0.88)',
        boxShadow: '0 6px 18px rgba(2,6,23,0.35)',
        color: '#fff',
        fontFamily: WIDE_FONT_FAMILY,
        fontSize: isCompact ? '11px' : '12px',
        fontWeight: 700,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        pointerEvents: 'auto',
        ...style,
      }}
    >
      <ClipboardList size={isCompact ? 13 : 15} aria-hidden />
      {/* 「匿名」は回答のハードルを下げる要素なので狭い画面でも残す。短い方は他の型の帯と同じ文言 */}
      {isCompact ? '匿名アンケート' : '匿名アンケートに回答'}
      <span style={{ opacity: 0.75, fontWeight: 600 }}>（全{total}問）</span>
    </button>
  )
}

export default WideSurveyEntryButton
